import { MutableRefObject, useCallback, useContext, useEffect, useRef, useState } from "react";

import {
  Button,
  getUserIdFromRefreshToken,
  Spinner,
  SpinnerCenter,
  useRemoteConfig,
  VideoStarted,
} from "@shapr3d/frontend-lib";
import { MessageDescriptor } from "react-intl";

import print3DIllustration from "../../assets/images/intentPage/3DPrint.webp";
import communicateIllustration from "../../assets/images/intentPage/communicate.webp";
import fixOnTheSpotIllustration from "../../assets/images/intentPage/fixOnTheSpot.webp";
import modelAnywhereIllustration from "../../assets/images/intentPage/modelAnywhere.webp";
import renderIllustration from "../../assets/images/intentPage/render.webp";
import reviewInARIllustration from "../../assets/images/intentPage/reviewInAR.webp";
import { localStorageKeys } from "../../config";
import useNavigation from "../../hooks/navigation";
import { useSetAppProperties } from "../../hooks/useGetAppProperties";
import usePrevious from "../../hooks/usePrevious";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import messages from "../../locales/messages";
import Heading from "../Heading/Heading";
import { WistiaAPI, WistiaVideo, WistiaVideoContext } from "../WistiaVideo/WistiaVideo";

import style from "./IntentPage.module.css";

type Video = {
  id: string;
  telemetryID: VideoStarted["eventProperties"]["video_id"];
  title: MessageDescriptor;
  thumbUrl: string;
  description: MessageDescriptor;
};

const videos = [
  {
    id: "zf9ddj0vn7",
    telemetryID: "model_parts",
    title: messages.IntentPage.Videos.ModelParts.Title,
    description: messages.IntentPage.Videos.ModelParts.Description,
    thumbUrl: print3DIllustration,
  },
  {
    id: "dh7nieyfz8",
    telemetryID: "model_anywhere",
    title: messages.IntentPage.Videos.ModelAnywhere.Title,
    description: messages.IntentPage.Videos.ModelAnywhere.Description,
    thumbUrl: modelAnywhereIllustration,
  },
  {
    id: "fajc41p3wt",
    telemetryID: "communicate_design_intent",
    title: messages.IntentPage.Videos.CommunicateDesignIntent.Title,
    description: messages.IntentPage.Videos.CommunicateDesignIntent.Description,
    thumbUrl: communicateIllustration,
  },
  {
    id: "2jbbyoziow",
    telemetryID: "review_edit_ar",
    title: messages.IntentPage.Videos.ReviewEditAR.Title,
    description: messages.IntentPage.Videos.ReviewEditAR.Description,
    thumbUrl: reviewInARIllustration,
  },
  {
    id: "chmzis79pz",
    telemetryID: "generative_render",
    title: messages.IntentPage.Videos.GenerativeRender.Title,
    description: messages.IntentPage.Videos.GenerativeRender.Description,
    thumbUrl: renderIllustration,
  },
  {
    id: "fe7v3hwirk",
    telemetryID: "fix_cad_import",
    title: messages.IntentPage.Videos.FixCADImport.Title,
    description: messages.IntentPage.Videos.FixCADImport.Description,
    thumbUrl: fixOnTheSpotIllustration,
  },
] satisfies Video[];

const videosV2 = [
  {
    id: "azypuck1q9",
    telemetryID: "model_parts",
    title: messages.IntentPage.Videos.ModelParts.Title,
    description: messages.IntentPage.Videos.ModelParts.Description,
    thumbUrl: print3DIllustration,
  },
  {
    id: "msy64fifjx",
    telemetryID: "model_anywhere",
    title: messages.IntentPage.Videos.ModelAnywhere.Title,
    description: messages.IntentPage.Videos.ModelAnywhere.Description,
    thumbUrl: modelAnywhereIllustration,
  },
  {
    id: "q2l2jvhd4v",
    telemetryID: "communicate_design_intent",
    title: messages.IntentPage.Videos.CommunicateDesignIntent.Title,
    description: messages.IntentPage.Videos.CommunicateDesignIntent.Description,
    thumbUrl: communicateIllustration,
  },
  {
    id: "7sny1xupdq",
    telemetryID: "review_edit_ar",
    title: messages.IntentPage.Videos.ReviewEditAR.Title,
    description: messages.IntentPage.Videos.ReviewEditAR.Description,
    thumbUrl: reviewInARIllustration,
  },
  {
    id: "8z1hlgmltb",
    telemetryID: "generative_render",
    title: messages.IntentPage.Videos.GenerativeRender.Title,
    description: messages.IntentPage.Videos.GenerativeRender.Description,
    thumbUrl: renderIllustration,
  },
  {
    id: "nduvhhq40e",
    telemetryID: "fix_cad_import",
    title: messages.IntentPage.Videos.FixCADImport.Title,
    description: messages.IntentPage.Videos.FixCADImport.Description,
    thumbUrl: fixOnTheSpotIllustration,
  },
] satisfies Video[];

function formatVideoDuration(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.round(seconds) % 60;
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}

const Card = ({
  onClick,
  thumbUrl,
  title,
  description,
}: {
  onClick: () => void;
  thumbUrl: string;
  title: MessageDescriptor;
  description: MessageDescriptor;
}) => {
  const { t } = useTranslation();
  const { isLoaded } = useContext(WistiaVideoContext);
  return (
    <div
      className={style.card}
      onClick={() => {
        if (!isLoaded) return;
        onClick();
      }}
    >
      <Thumbnail src={thumbUrl} alt={t(title)} />
      <div>
        <div className={style.videoTitle}>{t(title)}</div>
        <div className={style.videoDescription}>{t(description)}</div>
      </div>
    </div>
  );
};

const Thumbnail: React.FC<React.ImgHTMLAttributes<HTMLImageElement>> = (props) => {
  const { isLoaded, video } = useContext(WistiaVideoContext);

  return (
    <div
      className={style.imageContainer}
      onClick={(e) => {
        props.onClick?.(e as React.MouseEvent<HTMLImageElement>);
      }}
    >
      {isLoaded && (
        <div className={style.videoLengthLabel}>{formatVideoDuration(video?.duration() ?? 0)}</div>
      )}
      <img className={style.thumbnail} src={props.src} alt={props.alt} />
      {!isLoaded && (
        <div className={style.center}>
          <Spinner />
        </div>
      )}
    </div>
  );
};

const cleanupSeenIntentPageUser = () => {
  const hasSeenIntentPageUserMap = JSON.parse(
    localStorage.getItem(localStorageKeys.hasSeenIntentPageUserMap) ?? "{}"
  );
  delete hasSeenIntentPageUserMap[getUserIdFromRefreshToken() ?? ""];

  localStorage.setItem(
    localStorageKeys.hasSeenIntentPageUserMap,
    JSON.stringify(hasSeenIntentPageUserMap)
  );
};

const useSeenIntentPage = () => {
  const [hasSeenIntentPage] = useState(() => {
    const userID = getUserIdFromRefreshToken() ?? "";
    const hasSeenIntentPageUserMap = JSON.parse(
      localStorage.getItem(localStorageKeys.hasSeenIntentPageUserMap) ?? "{}"
    );

    return hasSeenIntentPageUserMap[userID];
  });
  useEffect(() => {
    const hasSeenIntentPageUserMap = JSON.parse(
      localStorage.getItem(localStorageKeys.hasSeenIntentPageUserMap) ?? "{}"
    );
    hasSeenIntentPageUserMap[getUserIdFromRefreshToken() ?? ""] = true;

    localStorage.setItem(
      localStorageKeys.hasSeenIntentPageUserMap,
      JSON.stringify(hasSeenIntentPageUserMap)
    );
  }, []);

  return hasSeenIntentPage;
};

function VideoPlaylist({ videos, finish }: { videos: Video[]; finish: () => void }) {
  const tracker = useTracker();
  const isIntentPageV2 = useRemoteConfig("web_intent_page_v2-cd");
  const hasSeenIntentPage = useSeenIntentPage();

  const { t, m } = useTranslation();

  const isOnLoadTracked = useRef(false);

  const players = useRef<Array<React.MutableRefObject<WistiaAPI | null>>>([]);

  const [popoversVisible, setPopoversVisible] = useState<Record<string, boolean>>({});
  const previousPopoversVisible = usePrevious(popoversVisible);
  const isAnyPopoverVisible = Object.values(popoversVisible).some(Boolean);
  // Wistia's popoverhide event sometimes fire multiple times so we track it ourselves to avoid duplicate events
  useEffect(() => {
    if (!previousPopoversVisible) return;
    if (isAnyPopoverVisible) return;

    const closedPopovers = Object.keys(previousPopoversVisible).filter((key) => {
      const wasOpen = previousPopoversVisible[key];
      const isOpen = popoversVisible[key];
      return wasOpen && !isOpen;
    });

    if (closedPopovers.length > 0) {
      closedPopovers.forEach((id) => {
        const item = videos.find((item) => item.id === id);
        if (!item) return;

        tracker.trackVideoStopped({
          reason: "closed",
          videoId: item.telemetryID,
        });
      });
    }
  }, [popoversVisible, isAnyPopoverVisible, previousPopoversVisible, tracker, videos]);

  const playNextVideo = (currentIndex: number, reason: "autoPlay" | "nextClicked") => {
    const nextIndex = (currentIndex + 1) % players.current.length;

    tracker.trackVideoStopped({
      reason: reason === "autoPlay" ? "finished" : "next_clicked",
      videoId: videos[currentIndex].telemetryID,
    });
    tracker.trackVideoStarted({
      from: reason === "autoPlay" ? "auto_play" : "clicked_next",
      videoId: videos[nextIndex].telemetryID,
    });

    playNewVideo(nextIndex, currentIndex);
  };

  const playPreviousVideo = (currentIndex: number) => {
    const previousIndex = (currentIndex - 1 + players.current.length) % players.current.length;

    tracker.trackVideoStopped({
      reason: "previous_clicked",
      videoId: videos[currentIndex].telemetryID,
    });
    tracker.trackVideoStarted({
      from: "clicked_previous",
      videoId: videos[previousIndex].telemetryID,
    });

    playNewVideo(previousIndex, currentIndex);
  };

  const [hasAnyVideoPlayed, setHasAnyVideoPlayed] = useState(false);
  const canProgress = hasAnyVideoPlayed || (isIntentPageV2 === "treatment" && hasSeenIntentPage);

  const playNewVideo = useCallback((newIndex: number, currentIndex?: number) => {
    const video = players.current[newIndex]?.current;
    video?.popover.show();
    if (currentIndex !== undefined) {
      players.current[currentIndex]?.current?.popover.hide();
    }
    video?.play();
    setHasAnyVideoPlayed(true);
  }, []);

  return (
    <>
      <div className={style.playListContainer}>
        {videos.map(({ id, thumbUrl, title, description, telemetryID }, index) => (
          <div key={id}>
            <WistiaVideo
              videoID={id}
              ref={(playerRef: MutableRefObject<WistiaAPI | null>) => {
                players.current[index] = playerRef;
              }}
              onLoad={(video) => {
                if (!isOnLoadTracked.current) {
                  tracker.trackIntentPageLoaded();
                  isOnLoadTracked.current = true;
                }

                video.bind("end", () => {
                  playNextVideo(index, "autoPlay");
                });
                video.bind("popovershow", () => {
                  setPopoversVisible((prev) => ({ ...prev, [id]: true }));
                });
                video.bind("popoverhide", () => {
                  setPopoversVisible((prev) => ({ ...prev, [id]: false }));
                });
              }}
            >
              <Card
                onClick={() => {
                  tracker.trackVideoStarted({
                    from: "intent_page",
                    videoId: telemetryID,
                  });
                  playNewVideo(index);
                }}
                thumbUrl={thumbUrl}
                title={title}
                description={description}
              />
              <WistiaVideo.Caption>
                <div className={style.captionBackground}>
                  <Button
                    variant="grayscale"
                    className={style.captionButton}
                    onClick={() => {
                      playPreviousVideo(index);
                    }}
                  >
                    {t(m.IntentPage.Buttons.Previous)}
                  </Button>
                  <Button
                    variant="primary"
                    className={style.captionButton}
                    onClick={() => {
                      tracker.trackVideoStopped({
                        reason: "start_modeling_clicked",
                        videoId: telemetryID,
                      });
                      tracker.trackIntentPageFinished({ trigger: "video" });
                      finish();
                    }}
                  >
                    {t(m.IntentPage.Buttons.StartModeling)}
                  </Button>
                  <Button
                    variant="grayscale"
                    className={style.captionButton}
                    onClick={() => {
                      playNextVideo(index, "nextClicked");
                    }}
                  >
                    {t(m.IntentPage.Buttons.Next)}
                  </Button>
                </div>
              </WistiaVideo.Caption>
            </WistiaVideo>
          </div>
        ))}
      </div>
      {canProgress && !isAnyPopoverVisible && (
        <Button
          variant="primary"
          onClick={() => {
            tracker.trackIntentPageFinished({ trigger: "page" });
            finish();
          }}
        >
          {t(m.IntentPage.Buttons.StartModeling)}
        </Button>
      )}
      {isIntentPageV2 === "treatment" && !canProgress && !isAnyPopoverVisible && (
        <div className={style.footerNote}>{t(m.IntentPage.WatchNote)}</div>
      )}
    </>
  );
}

export const IntentPage = () => {
  const { t, m } = useTranslation();
  const navigation = useNavigation();
  const isIntentPageV2 = useRemoteConfig("web_intent_page_v2-cd");

  const { mutateAsync: mutateAppProperties, isIdle } = useSetAppProperties();
  const finishSurvey = () => {
    const finishedAt = new Date().toISOString();

    mutateAppProperties(
      {
        is_resegmentation_needed: false,
        segmentation_work_completed: true,
        segmentation_work_web_completed: true,
        segmentation_is_student_or_teacher: false,
        segmentation_v2_finished_at: finishedAt,
        segmentation_v2_progressive_profiling_finished_at: finishedAt,
      },
      {
        onSuccess: () => {
          cleanupSeenIntentPageUser();
          navigation.tryRedirectFromSearchParams();
        },
      }
    );
  };

  if (!isIdle || isIntentPageV2 === undefined) {
    return <SpinnerCenter />;
  }

  return (
    <div className={style.pageContainer}>
      <div className={style.title}>
        <Heading type="h1">{t(m.IntentPage.Title)}</Heading>
        <div>{t(m.IntentPage.Subtitle)}</div>
      </div>
      <VideoPlaylist
        videos={isIntentPageV2 === "treatment" ? videosV2 : videos}
        finish={finishSurvey}
      />
    </div>
  );
};
